import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '../store/auth';
import { dashboardService } from '../services/dashboard.service';
import PageHeader from '../components/ui/PageHeader';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import { KPICard } from '../components/dashboard/KPICard';
import { CategoryStatsCard } from '../components/dashboard/CategoryStatsCard';
import { TrendChart } from '../components/dashboard/TrendChart';
import { RecentActivityCard } from '../components/dashboard/RecentActivityCard';
import ClosedTicketsChart from '../components/dashboard/ClosedTicketsChart';
import ClosedTicketsLast30DaysChart from '../components/dashboard/ClosedTicketsLast30DaysChart';
import TokensUsage30DaysChart from '../components/dashboard/TokensUsage30DaysChart';
import TokensUsage6MonthsChart from '../components/dashboard/TokensUsage6MonthsChart';
import { TrendingUp, Clock, Target, Wifi, Calendar, Users, MessageSquare, Ticket, Mail, CircleUser as UserCircle, Megaphone, Contact, Gift, AlertCircle, BookOpen, Plug, CalendarCheck } from 'lucide-react';

function Dashboard() {
  const user = useAuthStore((state) => state.user);

  const { data: stats, isLoading: isStatsLoading } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: () => dashboardService.getStats(),
    refetchInterval: 30000,
  });

  const { data: kpis, isLoading: isKPIsLoading } = useQuery({
    queryKey: ['dashboard-kpis'],
    queryFn: () => dashboardService.getKPIs(),
    refetchInterval: 60000,
  });

  const { data: messagesTrend } = useQuery({
    queryKey: ['dashboard-trends-messages'],
    queryFn: () => dashboardService.getTrends('messages', 'daily', 7),
    refetchInterval: 300000,
  });

  const { data: ticketsTrend } = useQuery({
    queryKey: ['dashboard-trends-tickets'],
    queryFn: () => dashboardService.getTrends('tickets', 'daily', 7),
    refetchInterval: 300000,
  });

  const { data: leadsTrend } = useQuery({
    queryKey: ['dashboard-trends-leads'],
    queryFn: () => dashboardService.getTrends('leads', 'daily', 7),
    refetchInterval: 300000,
  });

  const { data: closedTicketsByMonth } = useQuery({
    queryKey: ['dashboard-closed-tickets-by-month'],
    queryFn: () => dashboardService.getClosedTicketsByMonth(),
    refetchInterval: 300000,
  });

  const { data: closedTicketsLast30Days } = useQuery({
    queryKey: ['dashboard-closed-tickets-last-30-days'],
    queryFn: () => dashboardService.getClosedTicketsLast30Days(),
    refetchInterval: 300000,
  });

  const { data: tokensUsageLast30Days } = useQuery({
    queryKey: ['dashboard-tokens-usage-last-30-days'],
    queryFn: () => dashboardService.getTokensUsageLast30Days(),
    refetchInterval: 300000,
  });

  const { data: tokensUsageLast6Months } = useQuery({
    queryKey: ['dashboard-tokens-usage-last-6-months'],
    queryFn: () => dashboardService.getTokensUsageLast6Months(),
    refetchInterval: 300000,
  });

  if (isStatsLoading || isKPIsLoading) {
    return (
      <div className="py-6">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center h-64">
            <LoadingSpinner size="lg" />
          </div>
        </div>
      </div>
    );
  }

  if (!stats || !kpis) {
    return (
      <div className="py-6">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="text-center text-gray-500">
            Erro ao carregar dados do dashboard
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-6 min-h-[calc(100vh-4rem)]">
      <div className="px-4 sm:px-6 lg:px-8 space-y-6">
        <PageHeader title={`Bem-vindo, ${user?.name || 'Usuário'}!`} />

        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-50 mb-4 tracking-tight">
            Indicadores de Desempenho
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <KPICard
              title="Taxa de Resolução"
              value={kpis.ticket_resolution_rate}
              unit="%"
              icon={Target}
              color="text-green-600"
            />
            <KPICard
              title="Tempo Médio Resolução"
              value={kpis.avg_resolution_hours}
              unit="h"
              icon={Clock}
              color="text-blue-600"
            />
            <KPICard
              title="Conversão de Leads"
              value={kpis.lead_conversion_rate}
              unit="%"
              icon={TrendingUp}
              color="text-purple-600"
            />
            <KPICard
              title="Uptime WhatsApp"
              value={kpis.whatsapp_uptime_percentage}
              unit="%"
              icon={Wifi}
              color="text-emerald-600"
            />
            <KPICard
              title="Taxa de Conclusão"
              value={kpis.appointment_completion_rate}
              unit="%"
              icon={Calendar}
              color="text-orange-600"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {closedTicketsLast30Days && Array.isArray(closedTicketsLast30Days.data) && closedTicketsLast30Days.data.length > 0 && (
            <div className="bg-white dark:bg-gray-900/80 backdrop-blur-sm rounded-2xl shadow-card border border-gray-200/60 dark:border-gray-800/60 p-6 hover:shadow-card-hover transition-all duration-200">
              <h2 className="text-base font-semibold text-gray-900 dark:text-gray-50 mb-4 tracking-tight">
                Tickets Fechados (Últimos 30 Dias)
              </h2>
              <ClosedTicketsLast30DaysChart data={closedTicketsLast30Days} />
            </div>
          )}

          {closedTicketsByMonth && Array.isArray(closedTicketsByMonth.data) && closedTicketsByMonth.data.length > 0 && (
            <div className="bg-white dark:bg-gray-900/80 backdrop-blur-sm rounded-2xl shadow-card border border-gray-200/60 dark:border-gray-800/60 p-6 hover:shadow-card-hover transition-all duration-200">
              <h2 className="text-base font-semibold text-gray-900 dark:text-gray-50 mb-4 tracking-tight">
                Tickets Fechados (Últimos 6 Meses)
              </h2>
              <ClosedTicketsChart data={closedTicketsByMonth.data} />
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {tokensUsageLast30Days && Array.isArray(tokensUsageLast30Days.data) && tokensUsageLast30Days.data.length > 0 && (
            <div className="bg-white dark:bg-gray-900/80 backdrop-blur-sm rounded-2xl shadow-card border border-gray-200/60 dark:border-gray-800/60 p-6 hover:shadow-card-hover transition-all duration-200">
              <h2 className="text-base font-semibold text-gray-900 dark:text-gray-50 mb-4 tracking-tight">
                Uso de Tokens (Últimos 30 Dias)
              </h2>
              <TokensUsage30DaysChart data={tokensUsageLast30Days} />
            </div>
          )}

          {tokensUsageLast6Months && Array.isArray(tokensUsageLast6Months.data) && tokensUsageLast6Months.data.length > 0 && (
            <div className="bg-white dark:bg-gray-900/80 backdrop-blur-sm rounded-2xl shadow-card border border-gray-200/60 dark:border-gray-800/60 p-6 hover:shadow-card-hover transition-all duration-200">
              <h2 className="text-base font-semibold text-gray-900 dark:text-gray-50 mb-4 tracking-tight">
                Uso de Tokens (Últimos 6 Meses)
              </h2>
              <TokensUsage6MonthsChart data={tokensUsageLast6Months} />
            </div>
          )}
        </div>

        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-50 mb-4 tracking-tight">
            Visão Geral do Sistema
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            <CategoryStatsCard
              title="Usuários"
              icon={Users}
              color="text-blue-600"
              link="/user-accounts"
              stats={[
                { label: 'Total de Contas', value: stats.users.total_accounts, highlight: true },
                { label: 'Contas Ativas', value: stats.users.active_accounts },
                { label: 'Com Login', value: stats.users.accounts_with_login },
                { label: 'Perfis Distintos', value: stats.users.distinct_profiles },
              ]}
            />

            <CategoryStatsCard
              title="Mensagens"
              icon={MessageSquare}
              color="text-green-600"
              stats={[
                { label: 'Total', value: stats.messages.total_messages, highlight: true },
                { label: 'Recebidas', value: stats.messages.received_messages },
                { label: 'Enviadas', value: stats.messages.sent_messages },
                { label: 'Filas', value: stats.messages.total_queues },
              ]}
            />

            <CategoryStatsCard
              title="WhatsApp"
              icon={MessageSquare}
              color="text-emerald-600"
              link="/sessions"
              stats={[
                { label: 'Sessões Totais', value: stats.whatsapp.total_sessions, highlight: true },
                { label: 'Conectadas', value: stats.whatsapp.connected_sessions },
                { label: 'Desconectadas', value: stats.whatsapp.disconnected_sessions },
                { label: 'Engines', value: stats.whatsapp.distinct_engines },
              ]}
            />

            <CategoryStatsCard
              title="Tickets"
              icon={Ticket}
              color="text-red-600"
              stats={[
                { label: 'Total', value: stats.messages.total_tickets, highlight: true },
                { label: 'Abertos', value: stats.messages.open_tickets },
                { label: 'Fechados', value: stats.messages.closed_tickets },
                { label: 'Pendentes', value: stats.messages.pending_tickets },
              ]}
            />

            <CategoryStatsCard
              title="Assistentes"
              icon={UserCircle}
              color="text-purple-600"
              link="/assistants"
              stats={[
                { label: 'Total', value: stats.assistants.total_assistants, highlight: true },
                { label: 'Sempre Ativos', value: stats.assistants.always_active },
                { label: 'Ativados por Gatilho', value: stats.assistants.triggered },
                { label: 'Com Resumo', value: stats.assistants.with_resume },
              ]}
            />

            <CategoryStatsCard
              title="Contatos"
              icon={Contact}
              color="text-cyan-600"
              link="/contacts"
              stats={[
                { label: 'Total', value: stats.contacts.total_contacts, highlight: true },
                { label: 'Grupos', value: stats.contacts.groups },
                { label: 'Individuais', value: stats.contacts.individuals },
                { label: 'Chatbot Ativo', value: stats.contacts.chatbot_enabled },
              ]}
            />

            <CategoryStatsCard
              title="Campanhas"
              icon={Megaphone}
              color="text-pink-600"
              link="/campaigns"
              stats={[
                { label: 'Total', value: stats.campaigns.total_campaigns, highlight: true },
                { label: 'Ativas', value: stats.campaigns.active_campaigns },
                { label: 'Total Leads', value: stats.campaigns.total_leads },
                { label: 'Leads Convertidos', value: stats.campaigns.converted_leads },
              ]}
            />

            <CategoryStatsCard
              title="Email"
              icon={Mail}
              color="text-indigo-600"
              link="/email-management"
              stats={[
                { label: 'Total', value: stats.email.total_emails, highlight: true },
                { label: 'Não Lidos', value: stats.email.unread_emails },
                { label: 'Enviados', value: stats.email.sent_outbound },
                { label: 'Pendentes', value: stats.email.pending_outbound },
              ]}
            />

            <CategoryStatsCard
              title="Compromissos"
              icon={Calendar}
              color="text-orange-600"
              link="/appointments"
              stats={[
                { label: 'Total', value: stats.appointments.total_appointments, highlight: true },
                { label: 'Próximos', value: stats.appointments.upcoming_appointments },
                { label: 'Recorrentes', value: stats.appointments.recurring_appointments },
                { label: 'Ocorrências', value: stats.appointments.total_occurrences },
              ]}
            />

            <CategoryStatsCard
              title="Promoções ACE"
              icon={Gift}
              color="text-rose-600"
              link="/promotions"
              stats={[
                { label: 'Total', value: stats.ace.total_promotions, highlight: true },
                { label: 'Ativas', value: stats.ace.active_promotions },
                { label: 'Participantes', value: stats.ace.total_participants },
                { label: 'Cupons Gerados', value: stats.ace.generated_coupons },
              ]}
            />

            <CategoryStatsCard
              title="Fallback"
              icon={AlertCircle}
              color="text-amber-600"
              link="/fallback-requests"
              stats={[
                { label: 'Total', value: stats.fallback.total_requests, highlight: true },
                { label: 'Pendentes', value: stats.fallback.pending_requests },
                { label: 'Respondidas', value: stats.fallback.responded_requests },
                { label: 'Resp. Humana', value: stats.fallback.with_human_response },
              ]}
            />

            <CategoryStatsCard
              title="Calendário"
              icon={CalendarCheck}
              color="text-teal-600"
              link="/calendar-bookings"
              stats={[
                { label: 'Total Reservas', value: stats.calendar.total_bookings, highlight: true },
                { label: 'Confirmadas', value: stats.calendar.confirmed },
                { label: 'Canceladas', value: stats.calendar.canceled },
                { label: 'Profissionais', value: stats.calendar.total_professionals },
              ]}
            />

            <CategoryStatsCard
              title="Base Conhecimento"
              icon={BookOpen}
              color="text-violet-600"
              link="/knowledge-base"
              stats={[
                { label: 'Total Itens', value: stats.knowledge.total_items, highlight: true },
                { label: 'Categorias', value: stats.knowledge.distinct_categories },
              ]}
            />

            <CategoryStatsCard
              title="Integrações"
              icon={Plug}
              color="text-slate-600"
              link="/integrations"
              stats={[
                { label: 'Total', value: stats.integrations.total_integrations, highlight: true },
                { label: 'IXC', value: stats.integrations.ixc_integrations },
                { label: 'Hubsoft', value: stats.integrations.hubsoft_integrations },
                { label: 'SGP', value: stats.integrations.sgp_integrations },
              ]}
            />
          </div>
        </div>

        {(messagesTrend || ticketsTrend || leadsTrend) && (
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-50 mb-4 tracking-tight">
              Tendências (Últimos 7 Dias)
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {messagesTrend && (
                <TrendChart
                  title="Mensagens"
                  data={messagesTrend.data}
                  color="#10B981"
                />
              )}
              {ticketsTrend && (
                <TrendChart
                  title="Tickets"
                  data={ticketsTrend.data}
                  color="#EF4444"
                />
              )}
              {leadsTrend && (
                <TrendChart
                  title="Leads"
                  data={leadsTrend.data}
                  color="#8B5CF6"
                />
              )}
            </div>
          </div>
        )}

        {((stats.recent_activity?.tickets?.length ?? 0) > 0 || (stats.recent_activity?.messages?.length ?? 0) > 0) && (
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-50 mb-4 tracking-tight">
              Atividade Recente
            </h2>
            <RecentActivityCard
              tickets={stats.recent_activity?.tickets || []}
              messages={stats.recent_activity?.messages || []}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default Dashboard;
